import React, { useState } from "react";
import { View, TextInput, StyleSheet, Platform, StyleProp, ViewStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons, MaterialCommunityIcons } from "@expo/vector-icons";
import { Skeleton } from "@/components/ui/Skeleton";

interface CurrencyInputProps {
  label?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  required?: boolean;
  editable?: boolean;
  error?: string;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

function sanitize(text: string): string {
  // Strip everything except digits and a single decimal point
  let cleaned = text.replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }
  // Limit to 2 decimal places while typing
  if (parts.length === 2 && parts[1].length > 2) {
    cleaned = parts[0] + "." + parts[1].slice(0, 2);
  }
  return cleaned;
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || typeof value !== "number" || isNaN(value)) return "";
  return value.toFixed(2);
}

export function CurrencyInput({
  label,
  value,
  onChange,
  placeholder = "0.00",
  required = false,
  editable = true,
  error,
  loading = false,
  style,
  testID,
}: CurrencyInputProps) {
  const { colors } = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(formatCurrency(value));

  // Sync display when value changes externally (and input is not focused)
  const formattedExternal = formatCurrency(value);
  if (!isFocused && displayValue !== formattedExternal) {
    setDisplayValue(formattedExternal);
  }

  const hasError = !!error;

  if (loading) {
    return (
      <View style={[styles.container, style]}>
        {label && <Skeleton width={60} height={10} />}
        <Skeleton width="100%" height={40} radius={8} />
      </View>
    );
  }

  if (!editable) {
    return (
      <View style={[styles.container, style]}>
        {label && <ThemedText style={[styles.label, { color: colors.textSecondary }]}>{label}</ThemedText>}
        <View
          style={[
            styles.inputContainer,
            {
              borderColor: colors.border,
              borderWidth: StyleSheet.hairlineWidth,
              backgroundColor: colors.backgroundSecondary,
            },
          ]}
        >
          <MaterialCommunityIcons name="currency-usd" size={16} color={colors.textSecondary} />
          <ThemedText style={[styles.input, { color: value !== null ? colors.text : colors.placeholder }]}>
            {value !== null ? formatCurrency(value) : "—"}
          </ThemedText>
        </View>
      </View>
    );
  }

  const borderColor = hasError ? colors.ruby?.border || "red" : isFocused ? colors.primary : colors.border;
  const borderWidth = hasError || isFocused ? 1.5 : StyleSheet.hairlineWidth;

  const handleChangeText = (text: string) => {
    const cleaned = sanitize(text);
    setDisplayValue(cleaned);
    const num = parseFloat(cleaned);
    onChange(cleaned === "" ? null : isNaN(num) ? null : num);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (displayValue === "") {
      onChange(null);
      return;
    }
    const num = parseFloat(displayValue);
    if (isNaN(num)) {
      setDisplayValue("");
      onChange(null);
    } else {
      const rounded = Math.round(num * 100) / 100;
      setDisplayValue(rounded.toFixed(2));
      onChange(rounded);
    }
  };

  return (
    <View style={[styles.container, style]}>
      {(label || hasError) && (
        <View style={styles.labelRow}>
          {label && (
            <ThemedText style={[styles.label, { color: colors.textSecondary }]}>
              {label}
              {required && <ThemedText style={{ color: colors.ruby?.text || "red" }}> *</ThemedText>}
            </ThemedText>
          )}
          {hasError && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText || colors.ruby?.text || "red" }]}>
              {error}
            </ThemedText>
          )}
        </View>
      )}
      <View style={[styles.inputContainer, { borderColor, borderWidth, backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons
          name="currency-usd"
          size={16}
          color={hasError ? colors.ruby?.text || "red" : isFocused ? colors.primary : colors.textSecondary}
        />
        <TextInput
          testID={testID}
          value={displayValue}
          onChangeText={handleChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.placeholder}
          style={[styles.input, { color: colors.text }]}
          keyboardType="decimal-pad"
          editable={editable}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
        />
        {value !== null && value > 0 && !hasError && !isFocused && (
          <MaterialIcons name="check-circle" size={18} color={colors.emerald?.text || "green"} />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    minHeight: 40,
    gap: 8,
  },
  input: {
    flex: 1,
    fontSize: 13,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
    padding: 0,
  },
  errorText: {
    fontSize: 11,
    fontWeight: "500",
  },
});
